import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import React, { useCallback } from 'react';
import { Datepicker } from 'components/reusable/datepicker';
import { FilterPanel, hasActiveFilters } from 'components/reusable/filter-panel';
import { ReusableSelect as Select } from 'components/reusable/select';
import { useGlobalState } from 'store/reducers/reducer';
import { isValidDate } from 'utils/validation';
// Fields that represent an actual filter (excludes view config such as
// pageSize/page/tableView/includeResults, which always carry a value).
const FILTER_KEYS = [
    'status',
    'success',
    'accountGroupSid',
    'accountReviewed',
    'from',
    'to',
    'sortBy',
    'sortOrder',
    'respIdType',
    'respIdState',
    'ids',
    'respIdCountry',
    'lastName',
    'firstName',
    'is_disabled',
    'billableType',
    'billable',
    'aamvaSource',
    'type',
    'accountId',
    'aml',
    'email',
    'phone'
];
export const JobsFilter = ({ isAdmin, filterFields: filter, jobsOptions, onFilter, onReset }) => {
    const { state } = useGlobalState();
    const canClearFilters = hasActiveFilters(FILTER_KEYS.map((key) => filter[key]));
    const handleChangeFilters = useCallback((e) => {
        onFilter(e.target.name, e.target.value);
    }, [onFilter]);
    const handleChangeDateFilter = (date, fieldName) => {
        onFilter(fieldName, date && isValidDate(date)
            ? fieldName === 'from'
                ? new Date(new Date(date).setHours(0, 0, 0, 0))
                : new Date(new Date(date).setHours(23, 59, 59, 999))
            : null);
    };
    return (React.createElement(FilterPanel, { canClear: canClearFilters, onClear: onReset },
        React.createElement("div", { className: "filters-row row-fixed" },
            React.createElement(Select, { isClearable: false, items: jobsOptions.tableView, label: "View", name: "tableView", onChange: handleChangeFilters, value: filter.tableView })),
        React.createElement("div", { className: "filters-row" },
            React.createElement(Select, { items: jobsOptions.pageSize, label: "Size", name: "pageSize", onChange: handleChangeFilters, value: filter.pageSize }),
            React.createElement(Select, { items: jobsOptions.status, label: "Status", name: "status", onChange: handleChangeFilters, value: filter.status }),
            React.createElement(Select, { items: jobsOptions.success, label: "Success", name: "success", onChange: handleChangeFilters, value: filter.success }),
            React.createElement(Select, { items: jobsOptions.accountReviewed, label: "Reviewed", name: "accountReviewed", onChange: handleChangeFilters, value: filter.accountReviewed }),
            React.createElement(Select, { items: jobsOptions.sortBy, label: "Sort By", name: "sortBy", onChange: handleChangeFilters, value: filter.sortBy })),
        React.createElement("div", { className: "filters-row" },
            React.createElement(Select, { items: jobsOptions.sortOrder, label: "Order By", name: "sortOrder", onChange: handleChangeFilters, value: filter.sortOrder }),
            React.createElement(Select, { items: jobsOptions.billableType, label: "Billable Type", name: "billableType", onChange: handleChangeFilters, value: filter.billableType }),
            React.createElement(Select, { items: jobsOptions.aamvaSource, label: "Aamva Source", name: "aamvaSource", onChange: handleChangeFilters, value: filter.aamvaSource }),
            React.createElement(Select, { items: jobsOptions.type, label: "Job Type", name: "type", onChange: handleChangeFilters, value: filter.type })),
        React.createElement("div", { className: "filters-row row-fixed" },
            React.createElement(Datepicker, { date: filter.from, label: "From", maxDate: filter.to ?? undefined, onChange: (date) => {
                    handleChangeDateFilter(date, 'from');
                } }),
            React.createElement(Datepicker, { date: filter.to, label: "To", minDate: filter.from ?? undefined, onChange: (date) => {
                    handleChangeDateFilter(date, 'to');
                } }),
            React.createElement(Select, { items: jobsOptions.amLJobs, label: "AML", name: "aml", onChange: handleChangeFilters, value: filter.aml })),
        React.createElement("div", { className: "filters-row" },
            React.createElement(TextField, { label: "Type", name: "respIdType", onChange: handleChangeFilters, value: filter.respIdType }),
            React.createElement(TextField, { label: "State", name: "respIdState", onChange: handleChangeFilters, value: filter.respIdState }),
            React.createElement(TextField, { label: "Country", name: "respIdCountry", onChange: handleChangeFilters, value: filter.respIdCountry }),
            React.createElement(TextField, { label: "Last Name", name: "lastName", onChange: handleChangeFilters, value: filter.lastName }),
            React.createElement(TextField, { label: "First Name", name: "firstName", onChange: handleChangeFilters, value: filter.firstName })),
        React.createElement("div", { className: `filters-row ${isAdmin ? '' : 'row-fixed'}` },
            React.createElement(TextField, { label: "Ids", name: "ids", onChange: handleChangeFilters, value: filter.ids }),
            isAdmin && (React.createElement(Autocomplete, { debug: true, onChange: (_e, value) => {
                    if (state.accounts) {
                        const sid = value === null
                            ? null
                            : (state.accounts.find((a) => a.name === value)?.sid ??
                                null);
                        handleChangeFilters({
                            target: {
                                value: sid,
                                name: 'accountId'
                            }
                        });
                    }
                }, options: state.accounts
                    ? state.accounts.map((a) => a.name)
                    : filter.accountId === state.account?.sid
                        ? [state.account.name]
                        : [], renderInput: (params) => (React.createElement(TextField, { className: "g-full-width", label: "Account", ...params })), value: state.accounts?.find((a) => a.sid === filter.accountId)?.name ??
                    (filter.accountId === state.account?.sid
                        ? state.account.name
                        : null) })),
            isAdmin && (React.createElement(Select, { items: jobsOptions.is_disabled, label: "Account Status", name: "is_disabled", onChange: handleChangeFilters, value: filter.is_disabled })),
            isAdmin && (React.createElement(Select, { items: jobsOptions.billable, label: "Billable", name: "billable", onChange: handleChangeFilters, value: filter.billable || '' })),
            React.createElement(Select, { items: (state.accountGroups ?? []).map((a) => ({
                    id: a.sid,
                    name: a.name
                })), label: "Subgroup", name: "accountGroupSid", onChange: handleChangeFilters, value: filter.accountGroupSid || '' }),
            React.createElement(TextField, { label: "Contact Email", name: "email", onChange: handleChangeFilters, type: "email", value: filter.email }),
            React.createElement(TextField, { label: "Contact Phone", name: "phone", onChange: handleChangeFilters, value: filter.phone }))));
};
